import React, { useState, useEffect, useCallback } from 'react';
import { Menu, Search, Bell, Settings, LogOut, Plus, Download, Archive, Copy, Check, Home, Phone, Users, BarChart3, Zap, Clock, ClipboardList, Eye, EyeOff, UserPlus, Trash2, Edit3, Shield, User } from 'lucide-react';
import Stats from './Stats';
import NewCallModal from './NewCallModal';
import IncomingCallModal from './IncomingCallModal';

// URL de l'API - utilise l'origine actuelle (même serveur que le React)
const API_URL = window.location.origin;


// ============================================
// COMPOSANT PAGE DE CONNEXION
// ============================================
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await response.json();
      if (data.success) {
        onLogin(data.token, data.user);
      } else {
        setError(data.error || 'Identifiants incorrects');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="w-full max-w-md p-8 rounded-2xl shadow-2xl bg-slate-900 border border-slate-800">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 mb-4">
            <ClipboardList size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-blue-600">AutoFiches</h1>
          <p className="mt-2 text-sm text-slate-400">{"Connectez-vous pour acc\u00e9der \u00e0 l'application"}</p>
        </div>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center">{error}</div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-slate-300">Identifiant</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
              className="w-full px-4 py-3 rounded-lg border bg-slate-800 border-slate-700 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Votre identifiant" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-slate-300">Mot de passe</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                className="w-full px-4 py-3 pr-12 rounded-lg border bg-slate-800 border-slate-700 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Votre mot de passe" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button onClick={handleSubmit} disabled={loading || !username || !password}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed mt-2">
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COMPOSANT GESTION DES UTILISATEURS (ADMIN)
// ============================================
function UserManagement({ authFetch, darkMode, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '', displayName: '', role: 'technicien' });
  const [editForm, setEditForm] = useState({ displayName: '', role: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const bg = darkMode ? 'bg-slate-950' : 'bg-white';
  const bgAlt = darkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textMuted = darkMode ? 'text-slate-400' : 'text-slate-600';
  const border = darkMode ? 'border-slate-800' : 'border-slate-200';

  const fetchUsers = useCallback(async () => {
    try {
      const response = await authFetch('/api/admin/users');
      const data = await response.json();
      if (data.success) setUsers(data.users);
    } catch (err) { console.error('Erreur chargement utilisateurs:', err); }
    setLoadingUsers(false);
  }, [authFetch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    setError('');
    if (!newUser.username || !newUser.password || !newUser.displayName) { setError('Tous les champs sont requis'); return; }
    if (newUser.password.length < 6) { setError('Le mot de passe doit faire au moins 6 caractères'); return; }
    try {
      const response = await authFetch('/api/admin/users', { method: 'POST', body: JSON.stringify(newUser) });
      const data = await response.json();
      if (data.success) {
        setSuccess('Utilisateur créé avec succès'); setNewUser({ username: '', password: '', displayName: '', role: 'technicien' });
        setShowCreateForm(false); fetchUsers(); setTimeout(() => setSuccess(''), 3000);
      } else { setError(data.error); }
    } catch (err) { setError('Erreur de connexion'); }
  };

  const handleUpdate = async (id) => {
    setError('');
    const updates = {};
    if (editForm.displayName) updates.displayName = editForm.displayName;
    if (editForm.role) updates.role = editForm.role;
    if (editForm.password) {
      if (editForm.password.length < 6) { setError('Le mot de passe doit faire au moins 6 caractères'); return; }
      updates.password = editForm.password;
    }
    try {
      const response = await authFetch(`/api/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(updates) });
      const data = await response.json();
      if (data.success) { setSuccess('Utilisateur modifié'); setEditingUser(null); fetchUsers(); setTimeout(() => setSuccess(''), 3000); }
      else { setError(data.error); }
    } catch (err) { setError('Erreur de connexion'); }
  };

  const handleToggleActive = async (user) => {
    try {
      const response = await authFetch(`/api/admin/users/${user.id}`, { method: 'PUT', body: JSON.stringify({ active: !user.active }) });
      const data = await response.json();
      if (data.success) fetchUsers(); else setError(data.error);
    } catch (err) { setError('Erreur de connexion'); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer définitivement "${user.displayName}" ?`)) return;
    try {
      const response = await authFetch(`/api/admin/users/${user.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) { setSuccess('Utilisateur supprimé'); fetchUsers(); setTimeout(() => setSuccess(''), 3000); }
      else { setError(data.error); }
    } catch (err) { setError('Erreur de connexion'); }
  };

  if (loadingUsers) return <div className={`flex-1 flex items-center justify-center ${textMuted}`}>Chargement...</div>;

  return (
    <div className="flex-1 overflow-auto p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">Gestion des utilisateurs</h2>
            <p className={`text-sm ${textMuted} mt-1`}>{users.length} utilisateur{users.length > 1 ? 's' : ''}</p>
          </div>
          <button onClick={() => { setShowCreateForm(!showCreateForm); setError(''); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition flex items-center gap-2">
            <UserPlus size={18} /> Nouvel utilisateur
          </button>
        </div>

        {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}
        {success && <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm">{success}</div>}

        {showCreateForm && (
          <div className={`mb-6 p-6 rounded-xl border ${border} ${bgAlt}`}>
            <h3 className="font-semibold mb-4">Créer un utilisateur</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${textMuted}`}>Nom affiché</label>
                <input type="text" value={newUser.displayName} onChange={(e) => setNewUser({...newUser, displayName: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${border} ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} focus:outline-none focus:border-blue-500`} placeholder="Ex: Jean Dupont" />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${textMuted}`}>Identifiant</label>
                <input type="text" value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${border} ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} focus:outline-none focus:border-blue-500`} placeholder="Ex: jdupont" />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${textMuted}`}>Mot de passe</label>
                <input type="text" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${border} ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} focus:outline-none focus:border-blue-500`} placeholder="Min. 6 caractères" />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${textMuted}`}>Rôle</label>
                <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${border} ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} focus:outline-none focus:border-blue-500`}>
                  <option value="technicien">Technicien</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={handleCreate} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition">Créer</button>
              <button onClick={() => { setShowCreateForm(false); setError(''); }} className={`px-4 py-2 border ${border} rounded-lg text-sm font-medium hover:bg-slate-800 transition`}>Annuler</button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {users.map(user => (
            <div key={user.id} className={`p-4 rounded-xl border ${border} ${bgAlt} ${!user.active ? 'opacity-50' : ''}`}>
              {editingUser === user.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Nom affiché</label>
                      <input type="text" value={editForm.displayName} onChange={(e) => setEditForm({...editForm, displayName: e.target.value})}
                        className={`w-full px-3 py-2 rounded-lg border ${border} ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} focus:outline-none focus:border-blue-500 text-sm`} />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Rôle</label>
                      <select value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                        className={`w-full px-3 py-2 rounded-lg border ${border} ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} focus:outline-none focus:border-blue-500 text-sm`}>
                        <option value="technicien">Technicien</option>
                        <option value="admin">Administrateur</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Nouveau mot de passe (optionnel)</label>
                      <input type="text" value={editForm.password} onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                        className={`w-full px-3 py-2 rounded-lg border ${border} ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} focus:outline-none focus:border-blue-500 text-sm`}
                        placeholder="Laisser vide pour ne pas changer" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleUpdate(user.id)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition">Sauvegarder</button>
                    <button onClick={() => setEditingUser(null)} className={`px-3 py-1.5 border ${border} rounded-lg text-sm hover:bg-slate-800 transition`}>Annuler</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'admin' ? 'bg-amber-600' : 'bg-blue-600'} text-white font-bold`}>
                      {user.displayName ? user.displayName.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{user.displayName}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-amber-600/20 text-amber-400' : 'bg-blue-600/20 text-blue-400'}`}>
                          {user.role === 'admin' ? 'Admin' : 'Technicien'}
                        </span>
                        {!user.active && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-600/20 text-red-400">Désactivé</span>}
                      </div>
                      <div className={`text-sm ${textMuted}`}>@{user.username} · Dernière connexion : {user.lastLogin || 'Jamais'}</div>
                    </div>
                  </div>
                  {user.id !== currentUser.id && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setEditingUser(user.id); setEditForm({ displayName: user.displayName, role: user.role, password: '' }); setError(''); }}
                        className={`p-2 rounded-lg hover:bg-slate-800 transition ${textMuted}`} title="Modifier"><Edit3 size={16} /></button>
                      <button onClick={() => handleToggleActive(user)}
                        className={`p-2 rounded-lg hover:bg-slate-800 transition ${user.active ? 'text-orange-400' : 'text-green-400'}`}
                        title={user.active ? 'Désactiver' : 'Réactiver'}>{user.active ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                      <button onClick={() => handleDelete(user)} className="p-2 rounded-lg hover:bg-slate-800 transition text-red-400" title="Supprimer"><Trash2 size={16} /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default function FichesIntervention() {
  // ============================================
  // AUTHENTIFICATION (déclarés en premier, avant tous les autres hooks)
  // ============================================
  const [authToken, setAuthToken] = useState(() => sessionStorage.getItem('authToken'));
  const [currentUser, setCurrentUser] = useState(() => {
    const stored = sessionStorage.getItem('currentUser');
    return stored ? JSON.parse(stored) : null;
  });

  const authFetch = useCallback(async (url, options = {}) => {
    // Lire le token depuis sessionStorage (évite les problèmes de closure stale)
    const token = sessionStorage.getItem('authToken');
    if (!token) {
      return new Response(JSON.stringify({ success: false, error: 'Non connecté' }), {
        status: 401, headers: { 'Content-Type': 'application/json' }
      });
    }
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(options.headers || {})
      }
    });
    if (response.status === 401) {
      handleLogout();
      throw new Error('Session expirée');
    }
    return response;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Pas de dépendance sur authToken - on lit directement depuis sessionStorage

  const handleLogin = (token, user) => {
    setAuthToken(token);
    setCurrentUser(user);
    sessionStorage.setItem('authToken', token);
    sessionStorage.setItem('currentUser', JSON.stringify(user));
  };

  const handleLogout = () => {
    setAuthToken(null);
    setCurrentUser(null);
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('currentUser');
  };

  // ============================================
  // STATES EXISTANTS (tous déclarés inconditionnellement)
  // ============================================
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedFiche, setSelectedFiche] = useState(0);
  const [copied, setCopied] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [automationRunning, setAutomationRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [fiches, setFiches] = useState([]);
  const [loadingFiches, setLoadingFiches] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedClient, setEditedClient] = useState('');
  const [editedSujet, setEditedSujet] = useState('');
  const [editedDiagnostic, setEditedDiagnostic] = useState('');
  const [editedResultat, setEditedResultat] = useState('');
  const [savingFiche, setSavingFiche] = useState(false);
  const [validatingFiche, setValidatingFiche] = useState(false);
  const [justChangedFiche, setJustChangedFiche] = useState(false);
  const [filterStatut, setFilterStatut] = useState('Tous');
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingFile, setProcessingFile] = useState('');
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [archiveFilterDate, setArchiveFilterDate] = useState('all');
  const [selectedArchiveFiche, setSelectedArchiveFiche] = useState(null);
  const [archiveEditMode, setArchiveEditMode] = useState(false);
  const [archiveEditedClient, setArchiveEditedClient] = useState('');
  const [archiveEditedSujet, setArchiveEditedSujet] = useState('');
  const [archiveEditedDiagnostic, setArchiveEditedDiagnostic] = useState('');
  const [archiveEditedResultat, setArchiveEditedResultat] = useState('');
  const [isNewCallModalOpen, setIsNewCallModalOpen] = useState(false);
  const [isIncomingCallModalOpen, setIsIncomingCallModalOpen] = useState(false);
  const [incomingRecordingFile, setIncomingRecordingFile] = useState('');
  const [currentFicheFilename, setCurrentFicheFilename] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [pendingClientsQueue, setPendingClientsQueue] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedFicheRef = React.useRef(0);
  const editModeRef = React.useRef(false);
  const currentFicheFilenameRef = React.useRef(null);
  const previousSelectedFicheRef = React.useRef(0);
  const archiveScrollRef = React.useRef(null);

  // Synchroniser la ref avec editMode
  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  // Synchroniser la ref avec currentFicheFilename
  useEffect(() => {
    currentFicheFilenameRef.current = currentFicheFilename;
  }, [currentFicheFilename]);

  // Scroll en haut quand on change de filtre
  useEffect(() => {
    if (archiveScrollRef.current && currentPage === 'archives') {
      archiveScrollRef.current.scrollTop = 0;
    }
  }, [archiveFilterDate, currentPage]);

  // Récupérer la file d'attente des clients périodiquement
  useEffect(() => {
    const fetchPendingClients = () => {
      authFetch('/api/pending-clients-list')
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setPendingClientsQueue(data.clients || []);
          }
        })
        .catch(err => {
          console.error('Erreur récupération file d\'attente:', err);
        });
    };

    fetchPendingClients();
    const interval = setInterval(fetchPendingClients, 2000);
    return () => clearInterval(interval);
  }, []);

  // Vérifier l'état de l'automation au chargement
  useEffect(() => {
    authFetch('/api/automation-status')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setAutomationRunning(data.running);
        }
      })
      .catch(err => {
        console.error('Erreur lors de la vérification du statut:', err);
      });
  }, []);

  // Charger les fiches au démarrage et rafraîchir automatiquement
  useEffect(() => {
    // Fonction locale pour vérifier si une date est aujourd'hui
    const isToday = (dateStr) => {
      if (!dateStr) return false;
      const today = new Date();
      const [day, month, year] = dateStr.split('/');
      const ficheDate = new Date(year, month - 1, day);
      return ficheDate.toDateString() === today.toDateString();
    };

    const fetchFiches = () => {
      authFetch('/api/fiches')
        .then(res => res.json())
        .then(data => {
                        if (data.success && data.fiches) {
            setFiches(data.fiches);
            
            // Utiliser les refs pour avoir les valeurs actuelles
            const currentIndex = selectedFicheRef.current;
            const isCurrentlyEditing = editModeRef.current;
            const trackedFilename = currentFicheFilenameRef.current;
            
            // Si on a une fiche trackée, retrouver sa nouvelle position
            if (trackedFilename && data.fiches.length > 0) {
              const foundIndex = data.fiches.findIndex(f => f.filename === trackedFilename);
              if (foundIndex !== -1 && foundIndex !== currentIndex) {
                // Mettre à jour l'index sélectionné si la fiche a bougé
                console.log('📍 Fiche déplacée de', currentIndex, 'à', foundIndex);
                setSelectedFiche(foundIndex);
                selectedFicheRef.current = foundIndex;
              }
            }
            
            // Vérifier s'il y a des fiches aujourd'hui
            const fichesToday = data.fiches.filter(f => isToday(f.date));
            
            // Détecter s'il y a une VRAIMENT nouvelle fiche "Nouveau" aujourd'hui
            // (une fiche qu'on n'a jamais vue, pas juste une fiche avec statut Nouveau)
            const newNouveauFiche = fichesToday.find(f => 
              f.statut === 'Nouveau' && f.filename !== trackedFilename
            );
            
            // Auto-sélection UNIQUEMENT si :
            // 1. C'est une nouvelle fiche "Nouveau"
            // 2. On est sur le dashboard
            // 3. On n'a PAS encore de fiche sélectionnée (trackedFilename est null)
            //    OU on n'est pas encore initialisé
            if (newNouveauFiche && currentPage === 'dashboard' && !trackedFilename) {
              const newIndex = data.fiches.findIndex(f => f.filename === newNouveauFiche.filename);
              if (newIndex !== -1) {
                console.log('🆕 Nouvelle fiche "Nouveau" détectée, auto-sélection:', newNouveauFiche.filename);
                setSelectedFiche(newIndex);
                selectedFicheRef.current = newIndex;
                setCurrentFicheFilename(newNouveauFiche.filename);
                currentFicheFilenameRef.current = newNouveauFiche.filename;
                
                // Activer le mode édition et remplir les champs
                setEditedClient(newNouveauFiche.client === 'Non mentionné' ? '' : newNouveauFiche.client);
                setEditedSujet(newNouveauFiche.sujet);
                setEditedDiagnostic(newNouveauFiche.diagnostic);
                setEditedResultat(newNouveauFiche.resultat);
                setEditMode(true);
                editModeRef.current = true;
                
                setLoadingFiches(false);
                return; // Sortir ici pour ne pas exécuter le reste
              }
            }
            
            if (data.fiches.length === 0) {
              // Si plus aucune fiche, réinitialiser tous les champs
              setEditedClient('');
              setEditedSujet('');
              setEditedDiagnostic('');
              setEditedResultat('');
              setEditMode(false);
              setSelectedFiche(0);
              selectedFicheRef.current = 0;
              setCurrentFicheFilename(null);
            } else if (fichesToday.length === 0 && currentPage === 'dashboard') {
              // Si aucune fiche aujourd'hui sur le dashboard, réinitialiser
              setEditedClient('');
              setEditedSujet('');
              setEditedDiagnostic('');
              setEditedResultat('');
              setEditMode(false);
              setCurrentFicheFilename(null);
            }
            // Ne pas mettre à jour les champs ici - laissons le useEffect de selectedFiche le faire
          }
          setLoadingFiches(false);
        })
        .catch(err => {
          console.error('Erreur lors du chargement des fiches:', err);
          setLoadingFiches(false);
        });
    };

    // Toujours rafraîchir la liste (pour voir les nouvelles fiches)
    fetchFiches();
    const interval = setInterval(fetchFiches, 2000);
    return () => clearInterval(interval);
  }, [currentPage]);  // Pas editMode dans les dépendances

  // Initialiser le mode édition au premier chargement si la première fiche est "Nouveau"
  useEffect(() => {
    // On vérifie si c'est le premier chargement réussi et qu'on n'a pas encore initialisé
    if (!loadingFiches && fiches.length > 0 && !initialized) {
      const fiche = fiches[selectedFiche] || fiches[0];
      console.log('🎬 Initialisation au chargement:', fiche.statut, fiche.filename);
      
      // Marquer comme initialisé
      setInitialized(true);
      
      // Initialiser les champs avec la fiche
      setCurrentFicheFilename(fiche.filename);
      setEditedClient(fiche.client === 'Non mentionné' ? '' : fiche.client);
      setEditedSujet(fiche.sujet);
      setEditedDiagnostic(fiche.diagnostic);
      setEditedResultat(fiche.resultat);
      
      // Activer le mode édition si c'est une fiche "Nouveau"
      if (fiche.statut === 'Nouveau') {
        console.log('✏️ Activation du mode édition au chargement');
        setEditMode(true);
        editModeRef.current = true; // Synchroniser la ref immédiatement
      } else {
        setEditMode(false);
        editModeRef.current = false;
      }
    }
  }, [loadingFiches, fiches, initialized, selectedFiche]);

  // Polling pour récupérer le statut de traitement (tourne en permanence)
  useEffect(() => {
    const fetchProcessingStatus = () => {
      authFetch('/api/processing-status')
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setProcessingStatus(data.status);
            setProcessingFile(data.filename || '');
          }
        })
        .catch(err => {
          console.error('Erreur lors de la récupération du statut:', err);
        });
    };

    fetchProcessingStatus();
    const interval = setInterval(fetchProcessingStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  // Détecter les nouveaux enregistrements en cours (tourne en permanence)
  useEffect(() => {
    const checkRecording = () => {
      authFetch('/api/check-recording')
        .then(res => res.json())
        .then(data => {
          if (data.success && data.newRecording && !isIncomingCallModalOpen) {
            setIncomingRecordingFile(data.filename);
            setIsIncomingCallModalOpen(true);
          }
        })
        .catch(err => {
          console.error('Erreur lors de la vérification des enregistrements:', err);
        });
    };

    checkRecording();
    const interval = setInterval(checkRecording, 2000);
    return () => clearInterval(interval);
  }, [isIncomingCallModalOpen]);

  // Quand la fiche sélectionnée change, gérer le mode édition
  useEffect(() => {
    // Vérifier si c'est un vrai changement (utilisateur a cliqué) ou juste un rafraîchissement
    const hasUserChangedFiche = selectedFiche !== previousSelectedFicheRef.current;
    
    // Mettre à jour les refs
    previousSelectedFicheRef.current = selectedFiche;
    selectedFicheRef.current = selectedFiche;
    
    if (fiches.length > 0 && selectedFiche < fiches.length && fiches[selectedFiche]) {
      const fiche = fiches[selectedFiche];
      
      // Détecter si c'est une nouvelle fiche (filename différent)
      const isNewFiche = fiche.filename !== currentFicheFilename;
      
      // Utiliser la ref pour avoir la vraie valeur actuelle de editMode
      const isCurrentlyEditing = editModeRef.current;
      
      // Mettre à jour les champs si :
      // 1. On n'est PAS en mode édition, OU
      // 2. L'utilisateur a vraiment changé de fiche (clic volontaire), OU
      // 3. C'est une nouvelle fiche qui vient d'arriver (filename différent)
      if (!isCurrentlyEditing || hasUserChangedFiche || isNewFiche) {
        console.log('🔧 Mise à jour des champs:', {
          ficheName: fiche.filename,
          statut: fiche.statut,
          hasUserChangedFiche,
          isCurrentlyEditing,
          isNewFiche,
          currentFicheFilename
        });
        // Mettre à jour le filename actuel
        setCurrentFicheFilename(fiche.filename);
        // Mettre à jour les champs
        setEditedClient(fiche.client === 'Non mentionné' ? '' : fiche.client);
        setEditedSujet(fiche.sujet);
        setEditedDiagnostic(fiche.diagnostic);
        setEditedResultat(fiche.resultat);
        // Gérer le mode édition selon le statut de la fiche
        if (fiche.statut === 'Nouveau') {
          console.log('✏️ Activation du mode édition (statut Nouveau)');
          setEditMode(true);
        } else {
          console.log('🔒 Désactivation du mode édition (statut Validée)');
          setEditMode(false);
        }
      } else {
        console.log('🛡️ Protection des champs (édition en cours sans changement de fiche)');
        // En mode édition sans changement de fiche : ne toucher à rien
        // (protection de la saisie pendant le rafraîchissement automatique)
      }
    }
  }, [selectedFiche]);  // Seulement selectedFiche

  const handleIncomingCallSubmit = async (clientName) => {
    try {
      // Enregistrer le client en attente
      const response = await authFetch('/api/pending-client', {
        method: 'POST',
        body: JSON.stringify({ client: clientName })
      });
      
      if (response.ok) {
        // Réinitialiser la détection pour éviter de redétecter le même fichier
        await authFetch('/api/reset-recording-detection', {
          method: 'POST'
        });
        
        // Fermer la modal
        setIsIncomingCallModalOpen(false);
        setIncomingRecordingFile('');
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du client:', error);
    }
  };

  const copyToClipboard = async (text, id) => {
    // Convertir les \n en vrais sauts de ligne pour la copie
    const textWithLineBreaks = text.replace(/\\n/g, '\n');
    
    try {
      // Méthode moderne (fonctionne en HTTPS ou localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textWithLineBreaks);
      } else {
        // Fallback pour HTTP : utiliser un textarea temporaire
        const textArea = document.createElement('textarea');
        textArea.value = textWithLineBreaks;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      // Fallback ultime
      const textArea = document.createElement('textarea');
      textArea.value = textWithLineBreaks;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
      } catch (e) {
        console.error('Fallback copie échoué:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  const startAutomation = async () => {
    setLoading(true);
    setStatusMessage('');
    try {
      const response = await authFetch('/api/start-automation', {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        setAutomationRunning(true);
        setStatusMessage('✅ Automation lancée avec succès');
      } else {
        setStatusMessage('❌ Erreur : ' + data.error);
      }
    } catch (error) {
      setStatusMessage('❌ Erreur de connexion au serveur');
    }
    setLoading(false);
  };

  const stopAutomation = async () => {
    setLoading(true);
    setStatusMessage('');
    try {
      const response = await authFetch('/api/stop-automation', {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        setAutomationRunning(false);
        setStatusMessage('ℹ️ Automation arrêtée');
      } else {
        setStatusMessage('❌ Erreur : ' + data.error);
      }
    } catch (error) {
      setStatusMessage('❌ Erreur de connexion au serveur');
    }
    setLoading(false);
  };

  const saveFiche = async () => {
    if (fiches.length === 0) return;
    setSavingFiche(true);
    const fiche = fiches[selectedFiche];
    try {
      const response = await authFetch(`/api/fiches/${fiche.filename}`, {
        method: 'POST',
        body: JSON.stringify({
          client: editedClient.trim() === '' ? 'Non mentionné' : editedClient.trim(),
          sujet: editedSujet || '',
          diagnostic: editedDiagnostic || '',
          resultat: editedResultat || ''
        })
      });
      const data = await response.json();
      if (data.success) {
        // Rafraîchir les fiches pour obtenir les données à jour
        const refreshResponse = await authFetch('/api/fiches');
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setFiches(refreshData.fiches);
        }
        setEditMode(false);
      } else {
        alert('❌ Erreur : ' + data.error);
      }
    } catch (error) {
      alert('❌ Erreur de connexion');
    }
    setSavingFiche(false);
  };

  const validateFiche = async () => {
    if (fiches.length === 0) return;
    setValidatingFiche(true);
    const fiche = fiches[selectedFiche];
    try {
      // D'abord sauvegarder les modifications
      const saveResponse = await authFetch(`/api/fiches/${fiche.filename}`, {
        method: 'POST',
        body: JSON.stringify({
          client: editedClient.trim() === '' ? 'Non mentionné' : editedClient.trim(),
          sujet: editedSujet || '',
          diagnostic: editedDiagnostic || '',
          resultat: editedResultat || ''
        })
      });
      const saveData = await saveResponse.json();
      
      if (!saveData.success) {
        alert('❌ Erreur lors de la sauvegarde : ' + saveData.error);
        setValidatingFiche(false);
        return;
      }

      // Puis valider la fiche
      const response = await authFetch(`/api/fiches/${fiche.filename}/valider`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        // Rafraîchir immédiatement les fiches
        const refreshResponse = await authFetch('/api/fiches');
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setFiches(refreshData.fiches);
          setEditMode(false);
        }
      } else {
        alert('❌ Erreur : ' + data.error);
      }
    } catch (error) {
      alert('❌ Erreur de connexion');
    }
    setValidatingFiche(false);
  };

  const deleteFiche = async () => {
    if (fiches.length === 0) return;
    
    const fiche = fiches[selectedFiche];
    const confirm = window.confirm(`Êtes-vous sûr de vouloir supprimer cette fiche ?\n\nClient: ${fiche.client}\nDate: ${fiche.date} ${fiche.heure}`);
    
    if (!confirm) return;

    try {
      const response = await authFetch(`/api/fiches/${fiche.filename}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        // Rafraîchir immédiatement la liste des fiches
        const refreshResponse = await authFetch('/api/fiches');
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setFiches(refreshData.fiches);
          
          // Si on supprime la dernière fiche ou si la fiche supprimée était la dernière de la liste
          if (refreshData.fiches.length === 0) {
            setSelectedFiche(0);
            setEditMode(false);
            setEditedClient('');
            setEditedSujet('');
            setEditedDiagnostic('');
            setEditedResultat('');
          } else if (selectedFiche >= refreshData.fiches.length) {
            // Si l'index sélectionné est maintenant hors limites, sélectionner la dernière fiche
            const newIndex = refreshData.fiches.length - 1;
            setSelectedFiche(newIndex);
            const newFiche = refreshData.fiches[newIndex];
            setEditedClient(newFiche.client === 'Non mentionné' ? '' : newFiche.client);
            setEditedSujet(newFiche.sujet);
            setEditedDiagnostic(newFiche.diagnostic);
            setEditedResultat(newFiche.resultat);
            setEditMode(newFiche.statut === 'Nouveau');
          } else {
            // Sinon, garder le même index (affichera la fiche suivante)
            // Mettre à jour les données de la nouvelle fiche à cet index
            const newFiche = refreshData.fiches[selectedFiche];
            setEditedClient(newFiche.client === 'Non mentionné' ? '' : newFiche.client);
            setEditedSujet(newFiche.sujet);
            setEditedDiagnostic(newFiche.diagnostic);
            setEditedResultat(newFiche.resultat);
            setEditMode(newFiche.statut === 'Nouveau');
          }
        }
      } else {
        alert('❌ Erreur : ' + data.error);
      }
    } catch (error) {
      alert('❌ Erreur de connexion');
    }
  };

  const saveArchiveFiche = async () => {
    if (!selectedArchiveFiche) return;
    setSavingFiche(true);
    try {
      const response = await authFetch(`/api/fiches/${selectedArchiveFiche.filename}`, {
        method: 'POST',
        body: JSON.stringify({
          client: archiveEditedClient.trim() === '' ? 'Non mentionné' : archiveEditedClient.trim(),
          sujet: archiveEditedSujet || '',
          diagnostic: archiveEditedDiagnostic || '',
          resultat: archiveEditedResultat || ''
        })
      });
      const data = await response.json();
      if (data.success) {
        // Rafraîchir la liste des fiches
        const refreshResponse = await authFetch('/api/fiches');
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setFiches(refreshData.fiches);
          // Mettre à jour la fiche sélectionnée avec les nouvelles données
          const updatedFiche = refreshData.fiches.find(f => f.filename === selectedArchiveFiche.filename);
          if (updatedFiche) {
            setSelectedArchiveFiche(updatedFiche);
          }
          setArchiveEditMode(false);
        }
      } else {
        alert('❌ Erreur : ' + data.error);
      }
    } catch (error) {
      alert('❌ Erreur de connexion');
    }
    setSavingFiche(false);
  };

  const validateArchiveFiche = async () => {
    if (!selectedArchiveFiche) return;
    setValidatingFiche(true);
    try {
      // D'abord sauvegarder les modifications si on est en mode édition
      if (archiveEditMode) {
        const saveResponse = await authFetch(`/api/fiches/${selectedArchiveFiche.filename}`, {
          method: 'POST',
          body: JSON.stringify({
            client: archiveEditedClient.trim() === '' ? 'Non mentionné' : archiveEditedClient.trim(),
            sujet: archiveEditedSujet || '',
            diagnostic: archiveEditedDiagnostic || '',
            resultat: archiveEditedResultat || ''
          })
        });
        const saveData = await saveResponse.json();
        
        if (!saveData.success) {
          alert('❌ Erreur lors de la sauvegarde : ' + saveData.error);
          setValidatingFiche(false);
          return;
        }
      }

      // Puis valider la fiche
      const response = await authFetch(`/api/fiches/${selectedArchiveFiche.filename}/valider`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        // Rafraîchir immédiatement les fiches
        const refreshResponse = await authFetch('/api/fiches');
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setFiches(refreshData.fiches);
          // Mettre à jour la fiche sélectionnée avec les nouvelles données
          const updatedFiche = refreshData.fiches.find(f => f.filename === selectedArchiveFiche.filename);
          if (updatedFiche) {
            setSelectedArchiveFiche(updatedFiche);
          }
          setArchiveEditMode(false);
        }
      } else {
        alert('❌ Erreur : ' + data.error);
      }
    } catch (error) {
      alert('❌ Erreur de connexion');
    }
    setValidatingFiche(false);
  };

  const fiche = (() => {
    const selectedFicheData = fiches[selectedFiche];
    if (!selectedFicheData) return {};
    
    // Sur le dashboard, ne montrer que les fiches du jour
    if (currentPage === 'dashboard') {
      const today = new Date();
      if (!selectedFicheData.date) return {};
      const [day, month, year] = selectedFicheData.date.split('/');
      const ficheDate = new Date(year, month - 1, day);
      const isTodayFiche = ficheDate.toDateString() === today.toDateString();
      
      return isTodayFiche ? selectedFicheData : {};
    }
    
    return selectedFicheData;
  })();
  const bg = darkMode ? 'bg-slate-950' : 'bg-white';
  const bgAlt = darkMode ? 'bg-slate-900' : 'bg-slate-50';
  const text = darkMode ? 'text-slate-50' : 'text-slate-900';
  const textMuted = darkMode ? 'text-slate-400' : 'text-slate-600';
  const border = darkMode ? 'border-slate-800' : 'border-slate-200';

  // Filtrer les fiches par date
  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const [day, month, year] = dateStr.split('/');
    const ficheDate = new Date(year, month - 1, day);
    return ficheDate.toDateString() === today.toDateString();
  };

  const getFilteredArchiveFiches = () => {
    const now = new Date();
    
    let filtered = fiches.filter(f => {
      if (!f.date || f.date === 'Non précisée') return false; // Exclure les fiches sans date
      
      const [day, month, year] = f.date.split('/');
      const ficheDate = new Date(year, month - 1, day);
      
      switch(archiveFilterDate) {
        case 'all':
          return true;
        case 'today':
          return ficheDate.toDateString() === now.toDateString();
        case 'yesterday':
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          return ficheDate.toDateString() === yesterday.toDateString();
        case 'week':
          const weekAgo = new Date(now);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return ficheDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(now);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return ficheDate >= monthAgo;
        default:
          return true;
      }
    });

    // Appliquer le filtre de recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(f => {
        if (f.client && f.client.toLowerCase().includes(query)) return true;
        if (f.sujet && f.sujet.toLowerCase().includes(query)) return true;
        if (f.diagnostic && f.diagnostic.toLowerCase().includes(query)) return true;
        if (f.resultat && f.resultat.toLowerCase().includes(query)) return true;
        if (f.date && f.date.includes(query)) return true;
        if (f.heure && f.heure.toLowerCase().includes(query)) return true;
        return false;
      });
    }

    // Trier par date/heure décroissante (plus récent en premier)
    return filtered.sort((a, b) => {
      const getDateTime = (fiche) => {
        if (!fiche.date || fiche.date === 'Non précisée') {
          return new Date(0); // Les fiches sans date valide vont à la fin
        }
        
        const [day, month, year] = fiche.date.split('/');
        const [hour, minute] = (fiche.heure || '00h00').split('h');
        
        return new Date(
          parseInt(year), 
          parseInt(month) - 1, 
          parseInt(day), 
          parseInt(hour) || 0, 
          parseInt(minute) || 0
        );
      };
      
      return getDateTime(b) - getDateTime(a);
    });
  };

  const todayFiches = fiches.filter(f => isToday(f.date));

  // Fonction de filtrage par recherche
  const filterBySearch = (fichesToFilter) => {
    if (!searchQuery.trim()) return fichesToFilter;
    
    const query = searchQuery.toLowerCase().trim();
    
    return fichesToFilter.filter(f => {
      // Recherche dans le client
      if (f.client && f.client.toLowerCase().includes(query)) return true;
      // Recherche dans le sujet
      if (f.sujet && f.sujet.toLowerCase().includes(query)) return true;
      // Recherche dans le diagnostic
      if (f.diagnostic && f.diagnostic.toLowerCase().includes(query)) return true;
      // Recherche dans le résultat
      if (f.resultat && f.resultat.toLowerCase().includes(query)) return true;
      // Recherche dans la date (format JJ/MM/AAAA)
      if (f.date && f.date.includes(query)) return true;
      // Recherche dans l'heure
      if (f.heure && f.heure.toLowerCase().includes(query)) return true;
      
      return false;
    });
  };

  // ============================================
  // RENDU CONDITIONNEL : LOGIN ou APP
  // ============================================
  if (!authToken || !currentUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div className={`flex h-screen ${bg} ${text}`}>
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 ${bgAlt} border-r ${border} flex flex-col`}>
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          {sidebarOpen && <h1 className="text-lg font-bold text-blue-600">AutoFiches</h1>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-slate-800 rounded-lg transition">
            <Menu size={20} />
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {[
            { icon: ClipboardList, label: 'Fiches d\'intervention', active: currentPage === 'dashboard', page: 'dashboard' },
            { icon: Archive, label: 'Archives', active: currentPage === 'archives', page: 'archives' },
            { icon: BarChart3, label: 'Statistiques', active: currentPage === 'stats', page: 'stats' },
            { icon: Users, label: 'Clients', active: false, page: 'clients' },
            { icon: Zap, label: 'Automatisation', active: false, page: 'automation' },
            ...(currentUser.role === 'admin' ? [{ icon: Shield, label: 'Utilisateurs', active: currentPage === 'users', page: 'users' }] : [])
          ].map((item, i) => (
            <button 
              key={i} 
              onClick={() => item.page && setCurrentPage(item.page)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${item.active ? 'bg-blue-600 text-white' : `${textMuted} hover:bg-slate-800`}`}>
              <item.icon size={20} />
              {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800 space-y-2">
          {sidebarOpen && (
            <div className="flex items-center gap-3 px-4 py-2 mb-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentUser.role === 'admin' ? 'bg-amber-600' : 'bg-blue-600'} text-white text-sm font-bold`}>
                {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{currentUser.displayName}</p>
                <p className={`text-xs ${textMuted} truncate`}>{currentUser.role === 'admin' ? 'Administrateur' : 'Technicien'}</p>
              </div>
            </div>
          )}
          <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 transition">
            <Settings size={20} />
            {sidebarOpen && <span className="text-sm">Paramètres</span>}
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-slate-800 transition">
            <LogOut size={20} />
            {sidebarOpen && <span className="text-sm">Déconnexion</span>}
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className={`${bgAlt} border-b ${border} px-8 py-4 flex items-center justify-between gap-6`}>
          <div className="flex-1 max-w-md">
            <div className={`flex items-center gap-3 px-4 py-2 rounded-lg ${bg} border ${border} ${searchQuery ? 'border-blue-500' : ''}`}>
              <Search size={18} className={searchQuery ? 'text-blue-500' : textMuted} />
              <input 
                type="text" 
                placeholder="Rechercher par client, mot-clé, date..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`bg-transparent outline-none w-full ${text} placeholder:${textMuted}`} 
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className={`${textMuted} hover:text-white transition`}
                  title="Effacer la recherche"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button 
                onClick={() => setIsNewCallModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition flex items-center gap-2">
                <Phone size={18} />
                Nouvel appel
              </button>
              {pendingClientsQueue.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingClientsQueue.length}
                </span>
              )}
            </div>
            {pendingClientsQueue.length > 0 && (
              <div className="h-9 px-4 rounded-lg text-sm flex items-center gap-2 bg-orange-600 text-white font-medium">
                <span>File :</span>
                <span>{pendingClientsQueue.map(c => c.client).join(' → ')}</span>
                <button
                  onClick={() => {
                    if (window.confirm('Vider la file d\'attente ?')) {
                      authFetch('/api/pending-client', { method: 'DELETE' })
                        .then(res => res.json())
                        .then(data => {
                          if (data.success) {
                            setPendingClientsQueue([]);
                          }
                        })
                        .catch(err => console.error('Erreur vidage file:', err));
                    }
                  }}
                  className="ml-1 hover:bg-orange-700 rounded transition px-1"
                  title="Vider la file d'attente"
                >
                  ✕
                </button>
              </div>
            )}
            {processingStatus && (
              <div className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-blue-900 text-blue-200`}>
                <span className="animate-spin">⚙️</span>
                <span>{processingStatus}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <button className={`p-2 rounded-lg hover:${bgAlt} transition relative`}>
              <Bell size={20} className={textMuted} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <button onClick={() => setDarkMode(!darkMode)} className={`p-2 rounded-lg hover:${bgAlt} transition`}>
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={handleLogout} className={`p-2 rounded-lg hover:${bgAlt} transition`} title="Déconnexion">
              <LogOut size={20} className={textMuted} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex gap-6 p-8">
          {currentPage === 'dashboard' ? (
            <>
              <div className="w-80 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Fiches du jour</h2>
                  <button className="p-2 hover:bg-slate-800 rounded-lg transition">
                    <Plus size={18} className="text-blue-600" />
                  </button>
                </div>
                <div className={`flex gap-2 ${bgAlt} p-2 rounded-lg`}>
                  <button 
                    onClick={() => setFilterStatut('Tous')}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium whitespace-nowrap ${filterStatut === 'Tous' ? 'bg-blue-600 text-white' : textMuted}`}>
                    Tous ({todayFiches.length})
                  </button>
                  <button 
                    onClick={() => setFilterStatut('Nouveau')}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium whitespace-nowrap ${filterStatut === 'Nouveau' ? 'bg-blue-600 text-white' : textMuted}`}>
                    Nouveau ({todayFiches.filter(f => f.statut === 'Nouveau').length})
                  </button>
                  <button 
                    onClick={() => setFilterStatut('Validée')}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium whitespace-nowrap ${filterStatut === 'Validée' ? 'bg-blue-600 text-white' : textMuted}`}>
                    Validée ({todayFiches.filter(f => f.statut === 'Validée').length})
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto space-y-2">
                  {loadingFiches ? <div className={`p-4 text-center ${textMuted}`}>Chargement...</div> : (
                    (() => {
                      // Filtrer les fiches du jour par statut
                      let fichesFiltrees = filterStatut === 'Tous' 
                        ? [...todayFiches]
                        : todayFiches.filter(f => f.statut === filterStatut);
                      
                      // Appliquer le filtre de recherche
                      fichesFiltrees = filterBySearch(fichesFiltrees);
                      
                      // Trier par date/heure décroissante
                      fichesFiltrees.sort((a, b) => {
                        const getDateTime = (fiche) => {
                          if (fiche.date && fiche.heure) {
                            const [day, month, year] = fiche.date.split('/');
                            const [hour, minute] = fiche.heure.split('h');
                            return new Date(year, month - 1, day, hour, minute);
                          }
                          return new Date(0);
                        };
                        return getDateTime(b) - getDateTime(a);
                      });
                      
                      return fichesFiltrees.length === 0 ? (
                        <div className={`p-4 text-center ${textMuted}`}>
                          {searchQuery ? `Aucun résultat pour "${searchQuery}"` : 'Aucune fiche aujourd\'hui'}
                        </div>
                      ) : (
                        fichesFiltrees.map((f, i) => {
                          const indexOriginal = fiches.indexOf(f);
                          return (
                            <button key={i} onClick={() => setSelectedFiche(indexOriginal)} className={`w-full text-left p-4 rounded-lg border-2 transition ${selectedFiche === indexOriginal ? `border-blue-600 ${bgAlt}` : `border-transparent ${textMuted} hover:border-slate-600`}`}>
                              <div className="flex items-start justify-between mb-2">
                                <div className="font-medium text-sm">{f.client}</div>
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${f.statut === 'Validée' ? 'bg-green-900 text-green-200' : 'bg-orange-900 text-orange-200'}`}>
                                  {f.statut === 'Validée' ? '✅ Validée' : '⭐ Nouveau'}
                                </span>
                              </div>
                              <div className={`text-xs ${textMuted} mb-2`}>{f.date} • {f.heure}</div>
                              <div className="text-xs line-clamp-2">{f.sujet}</div>
                            </button>
                          );
                        })
                      );
                    })()
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {todayFiches.length === 0 ? <div className={`p-8 text-center ${textMuted}`}><p>Aucune fiche aujourd'hui</p></div> : (
                  <div className="space-y-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {editMode ? (
                          <div className="mb-4 max-w-md">
                            <label className={`block text-sm font-medium mb-2 ${textMuted}`}>Nom du client</label>
                            <input 
                              type="text" 
                              value={editedClient} 
                              onChange={(e) => setEditedClient(e.target.value)}
                              className={`w-full px-4 py-2 rounded-lg border ${border} ${bg} ${text} outline-none focus:border-blue-600`}
                              placeholder="Entrez le nom du client"
                            />
                          </div>
                        ) : (
                          <h1 className="text-2xl font-bold mb-2">{fiche.client}</h1>
                        )}
                        <div className={`flex items-center gap-4 ${textMuted}`}>
                          <div className="flex items-center gap-2">
                            <Clock size={16} />
                            <span className="text-sm">{fiche.date} à {fiche.heure}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Zap size={16} />
                            <span className="text-sm">Durée: {fiche.duree}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className="p-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition">
                          <Download size={18} />
                        </button>
                        <button className="p-3 rounded-lg hover:bg-slate-800 transition">
                          <Archive size={18} className={textMuted} />
                        </button>
                      </div>
                    </div>

                    {[
                      { label: 'Sujet / Problème', value: fiche.sujet, state: editedSujet, setState: setEditedSujet },
                      { label: 'Diagnostic / Actions', value: fiche.diagnostic, state: editedDiagnostic, setState: setEditedDiagnostic },
                      { label: 'Résultat / Solution', value: fiche.resultat, state: editedResultat, setState: setEditedResultat }
                    ].map((field, i) => (
                      <div key={i} className={`${bgAlt} rounded-lg p-6 border ${border}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-sm uppercase tracking-wide text-blue-600">{field.label}</h3>
                          <button onClick={() => copyToClipboard(editMode ? field.state : field.value, field.label)} className={`p-2 rounded transition ${copied === field.label ? 'text-green-500' : `${textMuted} hover:text-blue-600`}`}>
                            {copied === field.label ? <Check size={18} /> : <Copy size={18} />}
                          </button>
                        </div>
                        {editMode ? (
                          <textarea value={field.state} onChange={(e) => field.setState(e.target.value)} className={`w-full p-3 rounded-lg border ${border} ${bg} ${text} outline-none resize-none`} rows="5" />
                        ) : (
                          <p className={`text-sm leading-relaxed ${textMuted} whitespace-pre-wrap`}>{field.value}</p>
                        )}
                      </div>
                    ))}

                    <div className="flex gap-3 pt-4">
                      {!editMode ? (
                        <>
                          {fiche.statut === 'Nouveau' && (
                            <>
                              <button onClick={deleteFiche} className="flex-1 px-4 py-3 border border-red-600 text-red-500 rounded-lg font-medium hover:bg-red-950 transition">
                                🗑️ Supprimer
                              </button>
                            </>
                          )}
                          {fiche.statut === 'Validée' && (
                            <button onClick={() => { setEditMode(true); setEditedClient(fiche.client === 'Non mentionné' ? '' : fiche.client); setEditedSujet(fiche.sujet); setEditedDiagnostic(fiche.diagnostic); setEditedResultat(fiche.resultat); }} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition">
                              ✏️ Modifier
                            </button>
                          )}
                          {fiche.statut === 'Validée' && (
                            <button onClick={deleteFiche} className="flex-1 px-4 py-3 border border-red-600 text-red-500 rounded-lg font-medium hover:bg-red-950 transition">
                              🗑️ Supprimer
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {fiche.statut === 'Nouveau' && (
                            <>
                              <button onClick={validateFiche} disabled={validatingFiche} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:bg-gray-600">
                                {validatingFiche ? '⏳ Validation...' : '✅ Valider'}
                              </button>
                              <button onClick={saveFiche} disabled={savingFiche} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:bg-gray-600">
                                {savingFiche ? '💾 Sauvegarde...' : '💾 Sauvegarder'}
                              </button>
                              <button onClick={deleteFiche} className="flex-1 px-4 py-3 border border-red-600 text-red-500 rounded-lg font-medium hover:bg-red-950 transition">
                                🗑️ Supprimer
                              </button>
                            </>
                          )}
                          {fiche.statut === 'Validée' && (
                            <>
                              <button onClick={saveFiche} disabled={savingFiche} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:bg-gray-600">
                                {savingFiche ? '💾 Sauvegarde...' : '💾 Sauvegarder'}
                              </button>
                              <button onClick={() => setEditMode(false)} className="flex-1 px-4 py-3 border border-slate-600 rounded-lg font-medium hover:bg-slate-800 transition">
                                ❌ Annuler
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : currentPage === 'archives' ? (
            <div className="flex-1 flex gap-6">
              <div className={`flex-1 flex flex-col gap-6 ${selectedArchiveFiche ? 'w-2/3' : 'w-full'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold">Archives</h1>
                    <p className={`text-sm ${textMuted} mt-1`}>
                      {getFilteredArchiveFiches().length} fiche(s)
                    </p>
                  </div>
                  <div className={`flex gap-2 ${bgAlt} p-2 rounded-lg`}>
                    {[
                      { label: 'Tout', value: 'all' },
                      { label: "Aujourd'hui", value: 'today' },
                      { label: 'Hier', value: 'yesterday' },
                      { label: '7 derniers jours', value: 'week' },
                      { label: '30 derniers jours', value: 'month' }
                    ].map((filter, i) => (
                      <button
                        key={i}
                        onClick={() => setArchiveFilterDate(filter.value)}
                        className={`px-4 py-2 rounded text-sm font-medium whitespace-nowrap transition ${archiveFilterDate === filter.value ? 'bg-blue-600 text-white' : `${textMuted} hover:bg-slate-800`}`}>
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={`${bgAlt} rounded-lg border ${border} flex-1 overflow-hidden flex flex-col`}>
                  <div className="grid grid-cols-5 gap-4 p-4 pb-3 border-b border-slate-700 font-semibold text-sm">
                    <div>Client</div>
                    <div>Date / Heure</div>
                    <div>Sujet</div>
                    <div>Durée</div>
                    <div>Statut</div>
                  </div>
                  <div ref={archiveScrollRef} className="divide-y divide-slate-700 overflow-y-auto flex-1">
                    {getFilteredArchiveFiches().length === 0 ? (
                      <div className={`py-8 text-center ${textMuted}`}>Aucune fiche trouvée</div>
                    ) : (
                      getFilteredArchiveFiches().map((f, i) => {
                        const indexOriginal = fiches.indexOf(f);
                        const isSelected = selectedArchiveFiche && selectedArchiveFiche.filename === f.filename;
                        return (
                          <button 
                            key={i}
                            onClick={() => setSelectedArchiveFiche(f)}
                            className={`w-full grid grid-cols-5 gap-4 py-4 text-left transition rounded px-4 ${isSelected ? 'bg-blue-900/30 border-l-4 border-blue-600' : 'hover:bg-slate-700/30'}`}>
                            <div className="font-medium text-sm">{f.client}</div>
                            <div className="text-sm">{f.date} • {f.heure}</div>
                            <div className="text-sm line-clamp-1">{f.sujet}</div>
                            <div className="text-sm">{f.duree}</div>
                            <div>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${f.statut === 'Validée' ? 'bg-green-900 text-green-200' : 'bg-orange-900 text-orange-200'}`}>
                                {f.statut === 'Validée' ? '✅ Validée' : '⭐ Nouveau'}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {selectedArchiveFiche && (
                <div className={`w-1/3 ${bgAlt} rounded-lg p-6 border ${border} overflow-y-auto space-y-4`}>
                  <div className="flex items-start justify-between mb-4">
                    <h2 className="text-xl font-bold">
                      {archiveEditMode ? (
                        <input 
                          type="text" 
                          value={archiveEditedClient}
                          onChange={(e) => setArchiveEditedClient(e.target.value)}
                          placeholder="Nom du client"
                          className={`w-full px-3 py-2 rounded-lg ${bgAlt} border ${border} focus:outline-none focus:border-blue-600`}
                        />
                      ) : (
                        selectedArchiveFiche.client
                      )}
                    </h2>
                    <button 
                      onClick={() => {
                        setSelectedArchiveFiche(null);
                        setArchiveEditMode(false);
                      }}
                      className={`p-2 rounded-lg hover:bg-slate-800 transition ${textMuted}`}>
                      ✕
                    </button>
                  </div>

                  <div className={`space-y-2 text-sm ${textMuted}`}>
                    <div className="flex items-center gap-2">
                      <Clock size={16} />
                      <span>{selectedArchiveFiche.date} à {selectedArchiveFiche.heure}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap size={16} />
                      <span>Durée: {selectedArchiveFiche.duree}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${selectedArchiveFiche.statut === 'Validée' ? 'bg-green-900 text-green-200' : 'bg-orange-900 text-orange-200'}`}>
                        {selectedArchiveFiche.statut === 'Validée' ? '✅ Validée' : '⭐ Nouveau'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-slate-700">
                    <div>
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-blue-600 mb-2">Sujet / Problème</h3>
                      {archiveEditMode ? (
                        <textarea 
                          value={archiveEditedSujet}
                          onChange={(e) => setArchiveEditedSujet(e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg ${bgAlt} border ${border} focus:outline-none focus:border-blue-600 min-h-[100px]`}
                        />
                      ) : (
                        <p className={`text-sm leading-relaxed ${textMuted} whitespace-pre-wrap`}>{selectedArchiveFiche.sujet}</p>
                      )}
                    </div>

                    <div>
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-blue-600 mb-2">Diagnostic / Actions</h3>
                      {archiveEditMode ? (
                        <textarea 
                          value={archiveEditedDiagnostic}
                          onChange={(e) => setArchiveEditedDiagnostic(e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg ${bgAlt} border ${border} focus:outline-none focus:border-blue-600 min-h-[100px]`}
                        />
                      ) : (
                        <p className={`text-sm leading-relaxed ${textMuted} whitespace-pre-wrap`}>{selectedArchiveFiche.diagnostic}</p>
                      )}
                    </div>

                    <div>
                      <h3 className="font-semibold text-sm uppercase tracking-wide text-blue-600 mb-2">Résultat / Solution</h3>
                      {archiveEditMode ? (
                        <textarea 
                          value={archiveEditedResultat}
                          onChange={(e) => setArchiveEditedResultat(e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg ${bgAlt} border ${border} focus:outline-none focus:border-blue-600 min-h-[100px]`}
                        />
                      ) : (
                        <p className={`text-sm leading-relaxed ${textMuted} whitespace-pre-wrap`}>{selectedArchiveFiche.resultat}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4 border-t border-slate-700">
                    {archiveEditMode ? (
                      <>
                        {selectedArchiveFiche.statut === 'Nouveau' && (
                          <button 
                            onClick={validateArchiveFiche}
                            disabled={validatingFiche}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition text-sm disabled:bg-gray-600">
                            {validatingFiche ? '⏳ Validation...' : '✅ Valider'}
                          </button>
                        )}
                        <button 
                          onClick={saveArchiveFiche}
                          disabled={savingFiche}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition text-sm disabled:bg-gray-600">
                          {savingFiche ? '💾 Sauvegarde...' : '💾 Sauvegarder'}
                        </button>
                        <button 
                          onClick={() => {
                            setArchiveEditMode(false);
                            setArchiveEditedClient(selectedArchiveFiche.client === 'Non mentionné' ? '' : selectedArchiveFiche.client);
                            setArchiveEditedSujet(selectedArchiveFiche.sujet);
                            setArchiveEditedDiagnostic(selectedArchiveFiche.diagnostic);
                            setArchiveEditedResultat(selectedArchiveFiche.resultat);
                          }}
                          className="flex-1 px-4 py-2 border border-slate-600 rounded-lg font-medium hover:bg-slate-800 transition text-sm">
                          ❌ Annuler
                        </button>
                      </>
                    ) : (
                      <>
                        {selectedArchiveFiche.statut === 'Nouveau' && (
                          <button 
                            onClick={validateArchiveFiche}
                            disabled={validatingFiche}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition text-sm disabled:bg-gray-600">
                            {validatingFiche ? '⏳ Validation...' : '✅ Valider'}
                          </button>
                        )}
                        <button 
                          onClick={() => {
                            setArchiveEditedClient(selectedArchiveFiche.client === 'Non mentionné' ? '' : selectedArchiveFiche.client);
                            setArchiveEditedSujet(selectedArchiveFiche.sujet);
                            setArchiveEditedDiagnostic(selectedArchiveFiche.diagnostic);
                            setArchiveEditedResultat(selectedArchiveFiche.resultat);
                            setArchiveEditMode(true);
                          }}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition text-sm">
                          ✏️ Modifier
                        </button>
                        <button 
                          onClick={async () => {
                            const confirm = window.confirm(`Êtes-vous sûr de vouloir supprimer cette fiche ?\n\nClient: ${selectedArchiveFiche.client}\nDate: ${selectedArchiveFiche.date} ${selectedArchiveFiche.heure}`);
                            
                            if (!confirm) return;

                            try {
                              const response = await authFetch(`/api/fiches/${selectedArchiveFiche.filename}`, {
                                method: 'DELETE',
                              });
                              const data = await response.json();
                              if (data.success) {
                                // Rafraîchir immédiatement la liste des fiches
                                const refreshResponse = await authFetch('/api/fiches');
                                const refreshData = await refreshResponse.json();
                                if (refreshData.success) {
                                  setFiches(refreshData.fiches);
                                  setSelectedArchiveFiche(null);
                                  setArchiveEditMode(false);
                                }
                              } else {
                                alert('❌ Erreur : ' + data.error);
                              }
                            } catch (error) {
                              alert('❌ Erreur de connexion');
                            }
                          }}
                          className="flex-1 px-4 py-2 border border-red-600 text-red-500 rounded-lg font-medium hover:bg-red-950 transition text-sm">
                          🗑️ Supprimer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : currentPage === 'stats' ? (
            <Stats fiches={fiches} darkMode={darkMode} />
          ) : currentPage === 'users' && currentUser.role === 'admin' ? (
            <UserManagement authFetch={authFetch} darkMode={darkMode} currentUser={currentUser} />
          ) : (
            <div className={`flex-1 flex items-center justify-center ${textMuted}`}>
              <p>Page en construction...</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal Nouvel appel */}
      <NewCallModal 
        isOpen={isNewCallModalOpen} 
        onClose={() => setIsNewCallModalOpen(false)}
        darkMode={darkMode}
        authFetch={authFetch}
      />
      
      {/* Modal Appel entrant */}
      <IncomingCallModal
        isOpen={isIncomingCallModalOpen}
        onClose={() => setIsIncomingCallModalOpen(false)}
        onSubmit={handleIncomingCallSubmit}
        darkMode={darkMode}
        recordingFile={incomingRecordingFile}
      />
    </div>
  );
}